import { gql } from "@apollo/client";
import { OrderBy } from "../queryString";
import { curbsuiteTransactionLogEntryFragment } from "../fragments";

export const GET_PAYEE_TRANSACTIONS_PRICE_FILTER = { price: { _neq: 0 } };
export const GET_PAYEE_TRANSACTIONS_POTENTIAL_REVENUE_FILTER = { potential_revenue: { _neq: 0 } };

export const GET_CURBSUITE_TRANSACTIONS = gql`
  query GetCurbsuiteTransactions(
    $priceFilter: financial_transaction_log_no_balance_bool_exp!
    $dateRangeFilter: financial_transaction_log_no_balance_bool_exp!
    $filter: financial_transaction_log_no_balance_bool_exp!
    $limit: Int!
    $offset: Int!
    $orderBy: [financial_transaction_log_no_balance_order_by!]
  ) {
    transactions: financial_transaction_log_no_balance(
      where: { _and: [$dateRangeFilter, $filter, { _or: [{ first_park: { _eq: true } }, $priceFilter] }] }
      limit: $limit
      offset: $offset
      order_by: $orderBy
    ) {
      ...curbsuiteTransactionLogEntryFragment
    }
    aggregate: financial_transaction_log_no_balance_aggregate(
      where: { _and: [$dateRangeFilter, $filter, { _or: [{ first_park: { _eq: true } }, $priceFilter] }] }
    ) {
      aggregate {
        count
      }
    }
  }
  ${curbsuiteTransactionLogEntryFragment}
`;

export const GET_BUS_STATION_TRANSACTIONS = gql`
  query GetBusStationTransactions(
    $priceFilter: financial_transaction_log_no_balance_bool_exp!
    $dateRangeFilter: financial_transaction_log_no_balance_bool_exp!
    $filter: financial_transaction_log_no_balance_bool_exp!
    $limit: Int!
    $offset: Int!
    $orderBy: [financial_transaction_log_no_balance_order_by!]
  ) {
    transactions: financial_transaction_log_no_balance(
      where: {
        _and: [
          $dateRangeFilter
          {
            _or: [
              { transaction_type: { _eq: "park" } }
              {
                _and: [
                  { transaction_type: { _eq: "gateway_transaction" } }
                  { transaction: { gateway_transactions: { gateway_transaction_type: { _eq: permit } } } }
                ]
              }
              # AUT-6163: adjustments (chargebacks/credits) of either row type above.
              {
                _and: [
                  { transaction_type: { _eq: "adjustment" } }
                  {
                    _or: [
                      { reference_transaction_type: { _eq: "park" } }
                      { reference_gateway_transaction_type: { _eq: "permit" } }
                    ]
                  }
                ]
              }
            ]
          }
          $filter
          { _or: [{ first_park: { _eq: true } }, $priceFilter] }
        ]
      }
      limit: $limit
      offset: $offset
      order_by: $orderBy
    ) {
      ...curbsuiteTransactionLogEntryFragment
    }
    aggregate: financial_transaction_log_no_balance_aggregate(
      where: {
        _and: [
          $dateRangeFilter
          {
            _or: [
              { transaction_type: { _eq: "park" } }
              {
                _and: [
                  { transaction_type: { _eq: "gateway_transaction" } }
                  { transaction: { gateway_transactions: { gateway_transaction_type: { _eq: permit } } } }
                ]
              }
              # AUT-6163: adjustments (chargebacks/credits) of either row type above.
              {
                _and: [
                  { transaction_type: { _eq: "adjustment" } }
                  {
                    _or: [
                      { reference_transaction_type: { _eq: "park" } }
                      { reference_gateway_transaction_type: { _eq: "permit" } }
                    ]
                  }
                ]
              }
            ]
          }
          $filter
          { _or: [{ first_park: { _eq: true } }, $priceFilter] }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
  ${curbsuiteTransactionLogEntryFragment}
`;

export interface GetCurbsuiteTransactionsData {
  transactions: CurbsuiteTransactionLogEntry[];
  aggregate: {
    aggregate: {
      count: number;
    };
  };
}

/**
 * "trip_charge" is the airport (GSP) billable event: one LPR entry capture, priced from the
 * vehicle's class. It is the airport equivalent of a "park" row on the Transactions tab.
 *
 * "adjustment" (AUT-6163) is a ledger-only correction to an earlier charge -- a chargeback or a
 * credit. It has no gateway transaction of its own; it points at the charge it adjusts through the
 * reference_* columns, and its `price` is its wallet effect, read off its journals like any row.
 */
export type CurbsuiteTransactionType = "park" | "gateway_transaction" | "trip_charge" | "adjustment";

export interface CurbsuiteTransactionLogEntry {
  transactionId: string;
  firstPark: boolean;
  unregisteredVehicleViolation: boolean;
  policyViolation: boolean;
  transactionType: CurbsuiteTransactionType;
  eventType: "park" | "double_park" | "gateway_transaction" | "trip_charge" | "adjustment";
  potentialRevenue: number;
  price: number;
  tripFeeAmount: number | null;
  rateBasis: string | null;
  serviceFeeAmount: number;
  revenue: number;
  currency: string;
  addressCity: string | null;
  addressState: string | null;
  addressStreet: string | null;
  startTime: string | null;
  endTime: string | null;
  transactionTs: string;
  createdAt: string;
  assignedToAccountAtLocalDate: string;
  payerAccount: { organizationName: string | null } | null;
  transactionDate: string;
  /** The charge an adjustment adjusts; null on every other row. */
  referenceTransactionId: string | null;
  referenceTransactionIndex: number | null;
  /** The referenced charge's own transaction_type: "park" or "gateway_transaction". */
  referenceTransactionType: CurbsuiteTransactionType | null;
  /** The referenced gateway charge's type ("wallet_reload" or "permit"); null for a park reference. */
  referenceGatewayTransactionType: GatewayTransactionType | null;
  /**
   * The referenced charge's own log row, via the `referenced_transaction` relationship (AUT-6160);
   * null on every other row. Only the dates the month picker filters on are selected.
   */
  referencedTransaction: {
    transactionDate: string | null;
    assignedToAccountAtLocalDate: string | null;
  } | null;
  transaction: {
    index: number;
    /** An adjustment's free-text reason (e.g. "Chargeback - Authorize.net case 12345"); null on other rows. */
    description?: string | null;
    sessionStartedAtLocal: string | null;
    sessionEndedAtLocal: string | null;
    gatewayTransactions: {
      gatewayTransactionType: GatewayTransactionType;
    }[];
    registeredVehicle?: {
      licensePlate: string;
      stateCode: string;
    };
  };
}

export type GatewayTransactionType =
  | "initial_wallet_load"
  | "wallet_reload"
  | "payout"
  | "wallet_refund"
  | "session_payment"
  | "permit";

export type DateRangeFilter =
  | { transaction_date: { _gte: Date; _lt: Date } }
  | { assigned_to_account_at_local_date: { _gte: Date; _lt: Date } }
  // No month scope: an exact transaction-ID lookup (AUT-6163's "Adjusts #" link), whose target can
  // sit in any month -- a chargeback typically lands weeks after the charge it reverses.
  | Record<string, never>;

export interface GetCurbsuiteTransactionsVars {
  dateRangeFilter: DateRangeFilter;
  filter: GetCurbsuiteTransactionsFilter;
  priceFilter: typeof GET_PAYEE_TRANSACTIONS_PRICE_FILTER | typeof GET_PAYEE_TRANSACTIONS_POTENTIAL_REVENUE_FILTER;
  limit: number;
  offset: number;
  orderBy?: TransactionOrderBy[];
}

/**
 * The row types a tab shows: one transaction type, plus (AUT-6163) the adjustments whose referenced
 * charge belongs on that tab.
 */
export interface CurbsuiteTabScopeFilter {
  _or: Record<string, unknown>[];
}

/**
 * `_and` of the tab scope (see buildCurbsuiteBaseFilter) and the user's column filters, kept as
 * separate conjuncts so an "or" column filter can't collide with the tab scope's own `_or`.
 */
export interface GetCurbsuiteTransactionsFilter {
  _and: (CurbsuiteTabScopeFilter | Record<string, unknown>)[];
}

export interface GetBusStationTransactionsVars {
  dateRangeFilter: DateRangeFilter;
  filter: GetBusStationTransactionsFilter;
  priceFilter: typeof GET_PAYEE_TRANSACTIONS_PRICE_FILTER | typeof GET_PAYEE_TRANSACTIONS_POTENTIAL_REVENUE_FILTER;
  limit: number;
  offset: number;
  orderBy?: TransactionOrderBy[];
}

export interface GetBusStationTransactionsFilter {
  // No required transaction_type — the query WHERE clause handles type constraints.
  // Set this only when the user filters the "Type" column: "Dwell Fee" → "park", "Reconciliation Charge" →
  // "gateway_transaction", "Adjustment" → "adjustment"
  transaction_type?: { _eq: "park" } | { _eq: "gateway_transaction" } | { _eq: "adjustment" };
  transaction?: {
    index?: { _eq: number };
    registered_vehicle?: { license_plate: { _eq: string } };
  };
  payer_account?: { organization_name: { _ilike: string } };
  transaction_date?: { _eq: string } | { _lt: string } | { _gt: string };
  assigned_to_account_at_local_date?: { _eq: string } | { _lt: string } | { _gt: string };
  _and?: Record<string, unknown>[];
  _or?: Record<string, unknown>[];
}

export type TransactionOrderBy =
  | TransactionOrderByIndex
  | TransactionOrderByTimeRange
  | TransactionOrderByDate
  | TransactionOrderByAssignedToAccountAtLocal
  | TransactionOrderByTransactionId;

export interface TransactionOrderByIndex {
  created_at: OrderBy;
}

export interface TransactionOrderByTimeRange {
  end_time: OrderBy;
}

export interface TransactionOrderByDate {
  transaction_date: OrderBy;
}

export interface TransactionOrderByAssignedToAccountAtLocal {
  assigned_to_account_at_local: OrderBy;
}

export interface TransactionOrderByTransactionId {
  transaction_id: OrderBy;
}
